import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import * as Sentry from '@sentry/react';
import { Users, MessageCircle, BookOpen, Star, MessageSquare, Inbox, Mail, CheckCircle2, ArrowRight, Shield, AlertCircle, Bug, Sparkles, Heart, Zap, Trash2, AlertTriangle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import UserManagementTable from '@/components/UserManagementTable';
import FlagsPanel from '@/components/FlagsPanel';
import BetaTesterPanel from '@/components/BetaTesterPanel';

const FEEDBACK_ICONS = {
  bug: Bug,
  suggestion: Sparkles,
  praise: Heart,
  other: MessageSquare,
};
const FEEDBACK_COLORS = {
  bug: 'bg-red-100 text-red-700 border-red-200',
  suggestion: 'bg-gold/15 text-gold border-gold/30',
  praise: 'bg-moss/15 text-moss border-moss/30',
  other: 'bg-sand text-text-muted border-black/10',
};

export default function AdminDashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [feedback, setFeedback] = useState([]);
  const [openFlagsCount, setOpenFlagsCount] = useState(0);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  // Reset Test Data state — declared here (top-level) to satisfy rules-of-hooks.
  const [resetState, setResetState] = useState({ open: false, preview: null, loading: false, confirmText: '' });

  useEffect(() => {
    if (user && !user.is_admin) {
      toast.error('Admin access required');
      navigate('/');
      return;
    }
    const load = async () => {
      try {
        const [s, f, fl] = await Promise.all([
          api.get('/admin/stats'),
          api.get('/admin/feedback?limit=30'),
          api.get('/admin/flags?status=open&limit=1').catch(() => ({ data: { open_count: 0 } })),
        ]);
        setStats(s.data);
        setFeedback(f.data.feedback || []);
        setOpenFlagsCount(fl.data.open_count || 0);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Could not load admin data');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user, navigate]);

  const refreshOpenFlagsCount = async () => {
    try {
      const fl = await api.get('/admin/flags?status=open&limit=1');
      setOpenFlagsCount(fl.data.open_count || 0);
    } catch { /* noop */ }
  };

  if (loading || !stats) {
    return (
      <div className="space-y-8" data-testid="admin-loading">
        <div className="animate-fade-up">
          <p className="text-gold font-sans text-xs tracking-[0.3em] uppercase mb-3">Beta Operations</p>
          <h1 className="font-serif text-4xl sm:text-5xl font-light text-dark mystical-heading">Admin Dashboard</h1>
        </div>
        <p className="text-text-muted font-sans text-sm">Loading...</p>
      </div>
    );
  }

  const triggerFrontendSentryError = () => {
    // Throw asynchronously inside a Sentry span so:
    //   1. The error is captured by Sentry's global unhandled handler (creates an Issue)
    //   2. The synchronous React handler completes cleanly (ErrorBoundary NOT triggered)
    //   3. A performance span is also recorded
    Sentry.startSpan({ op: 'admin.test', name: 'admin_test_frontend_error' }, () => {
      setTimeout(() => {
        throw new Error('Sentry frontend test from Admin Dashboard');
      }, 50);
    });
    toast.success('Frontend test error sent to Sentry. Check your dashboard in ~10s.');
  };

  const triggerBackendSentryError = async () => {
    try {
      await api.get('/admin/sentry-debug');
      toast.error('Expected an error — but the call succeeded?');
    } catch (err) {
      const status = err.response?.status;
      if (status === 500) {
        toast.success('Backend test error sent to Sentry. Check your dashboard in ~10s.');
      } else {
        toast.error(`Unexpected response: ${status || 'no status'}`);
      }
    }
  };

  // ── Reset Test Data flow (preview → confirm) ───────────────────────────────
  const previewReset = async () => {
    setResetState(s => ({ ...s, loading: true }));
    try {
      const res = await api.get('/admin/test-data-preview');
      setResetState({ open: true, preview: res.data, loading: false, confirmText: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load preview');
      setResetState(s => ({ ...s, loading: false }));
    }
  };

  const executeReset = async () => {
    if (resetState.confirmText !== 'DELETE TEST DATA') {
      toast.error('Type exactly: DELETE TEST DATA');
      return;
    }
    setResetState(s => ({ ...s, loading: true }));
    try {
      const res = await api.post('/admin/reset-test-data', { confirm: 'DELETE TEST DATA' });
      toast.success(`Deleted ${res.data.deleted} test accounts + their data`);
      setResetState({ open: false, preview: null, loading: false, confirmText: '' });
      // Refresh stats + feedback + flags
      const [s, f] = await Promise.all([
        api.get('/admin/stats'),
        api.get('/admin/feedback?limit=30'),
      ]);
      setStats(s.data);
      setFeedback(f.data.feedback || []);
      refreshOpenFlagsCount();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reset failed');
      setResetState(s => ({ ...s, loading: false }));
    }
  };

  const verifyPct = stats.users.total
    ? Math.round((stats.users.verified / stats.users.total) * 100)
    : 0;
  const onboardPct = stats.users.total
    ? Math.round((stats.users.onboarded / stats.users.total) * 100)
    : 0;

  return (
    <div className="space-y-10" data-testid="admin-dashboard-page">
      {/* Header */}
      <div className="animate-fade-up">
        <div className="flex items-center gap-3 mb-3">
          <Shield size={16} className="text-gold" />
          <p className="text-gold font-sans text-xs tracking-[0.3em] uppercase">Beta Operations</p>
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl font-light text-dark mystical-heading">Admin Dashboard</h1>
        <p className="text-text-muted font-sans text-base mt-3 max-w-xl">
          Beta-test health snapshot. Refresh the page to pull fresh numbers.
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="animate-fade-up delay-100" data-testid="admin-tabs">
        <TabsList className="bg-bone/60 border border-black/8 rounded-xl p-1 h-auto inline-flex gap-1">
          <TabsTrigger
            value="overview"
            data-testid="admin-tab-overview"
            className="font-sans text-xs tracking-[0.2em] uppercase px-5 h-10 rounded-lg data-[state=active]:bg-dark data-[state=active]:text-bone data-[state=active]:shadow-sm"
          >
            Overview
          </TabsTrigger>
          <TabsTrigger
            value="users"
            data-testid="admin-tab-users"
            className="font-sans text-xs tracking-[0.2em] uppercase px-5 h-10 rounded-lg data-[state=active]:bg-dark data-[state=active]:text-bone data-[state=active]:shadow-sm"
          >
            Users
          </TabsTrigger>
          <TabsTrigger
            value="flags"
            data-testid="admin-tab-flags"
            className="font-sans text-xs tracking-[0.2em] uppercase px-5 h-10 rounded-lg data-[state=active]:bg-dark data-[state=active]:text-bone data-[state=active]:shadow-sm relative"
          >
            <span className="inline-flex items-center gap-2">
              <AlertTriangle size={12} className={openFlagsCount > 0 ? 'text-red-500' : 'text-current'} />
              Flags
              {openFlagsCount > 0 && (
                <Badge className="bg-red-100 text-red-700 border border-red-200 text-[10px] font-sans px-1.5 py-0 ml-1" data-testid="admin-tab-flags-badge">
                  {openFlagsCount}
                </Badge>
              )}
            </span>
          </TabsTrigger>
          <TabsTrigger
            value="beta"
            data-testid="admin-tab-beta"
            className="font-sans text-xs tracking-[0.2em] uppercase px-5 h-10 rounded-lg data-[state=active]:bg-dark data-[state=active]:text-bone data-[state=active]:shadow-sm"
          >
            Beta
          </TabsTrigger>
        </TabsList>

        {/* OVERVIEW TAB */}
        <TabsContent value="overview" className="mt-8 space-y-10 focus-visible:outline-none">
          {/* Stat Tiles */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="admin-stat-tiles">
            <StatTile icon={Users} label="Total Users" value={stats.users.total} hint={`+${stats.users.new_24h} in 24h`} />
            <StatTile icon={CheckCircle2} label="Email Verified" value={`${verifyPct}%`} hint={`${stats.users.verified} / ${stats.users.total}`} accent="moss" />
            <StatTile icon={Sparkles} label="Onboarded" value={`${onboardPct}%`} hint={`${stats.users.onboarded} completed`} />
            <StatTile icon={Inbox} label="New (7d)" value={stats.users.new_7d} hint="Recent signups" accent="moss" />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <StatTile icon={MessageCircle} label="Coaching Sessions" value={stats.engagement.conversations} />
            <StatTile icon={BookOpen} label="Journal Entries" value={stats.engagement.journal_entries} accent="moss" />
            <StatTile icon={Star} label="Signs Logged" value={stats.engagement.glimmers} />
          </div>

          {/* Sentry verification panel */}
          <div className="mystical-card p-6" data-testid="admin-sentry-panel">
            <div className="flex items-center gap-2 mb-3">
              <Zap size={16} className="text-gold" />
              <span className="text-gold font-sans text-xs tracking-[0.25em] uppercase">Sentry Connection</span>
            </div>
            <p className="font-sans text-sm text-text-muted leading-relaxed mb-4 max-w-2xl">
              Tap a button to send a deliberate test error to Sentry, then check your Sentry dashboard
              (Issues tab) within ~10 seconds. Useful after deploy or when verifying monitoring.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={triggerFrontendSentryError}
                data-testid="sentry-test-frontend-button"
                className="bg-dark hover:bg-dark/90 text-bone rounded-xl font-sans text-xs tracking-[0.25em] uppercase btn-hover h-11 px-5"
              >
                <Bug size={14} className="mr-2" /> Test Frontend Sentry
              </Button>
              <Button
                onClick={triggerBackendSentryError}
                data-testid="sentry-test-backend-button"
                variant="outline"
                className="border-gold/25 bg-bone/40 hover:bg-bone rounded-xl font-sans text-xs tracking-[0.25em] uppercase h-11 px-5"
              >
                <Bug size={14} className="mr-2" /> Test Backend Sentry
              </Button>
            </div>
            <p className="font-sans text-[10px] text-text-muted/70 mt-3">
              Frontend test → throws an error caught by ErrorBoundary. Backend test → calls
              <code className="mx-1 px-1 py-0.5 rounded bg-bone/60">/api/admin/sentry-debug</code>.
            </p>
          </div>

          {/* Reset Test Data panel */}
          <div className="mystical-card p-6 border-red-200/50" data-testid="admin-reset-panel">
            <div className="flex items-center gap-2 mb-3">
              <Trash2 size={16} className="text-red-600" />
              <span className="text-red-700 font-sans text-xs tracking-[0.25em] uppercase">Reset Test Data</span>
            </div>
            <p className="font-sans text-sm text-text-muted leading-relaxed mb-4 max-w-2xl">
              Wipes ALL non-admin, non-protected accounts and their related data (conversations,
              journal entries, signs, feedback, sessions). Use this to clear test pollution
              before launch or after demos. <span className="text-dark font-medium">Protected accounts are never touched.</span>
            </p>
            <Button
              onClick={previewReset}
              disabled={resetState.loading}
              data-testid="reset-preview-button"
              variant="outline"
              className="border-red-300 bg-red-50/40 hover:bg-red-50 text-red-700 rounded-xl font-sans text-xs tracking-[0.25em] uppercase h-11 px-5"
            >
              <Trash2 size={14} className="mr-2" /> Preview & Reset...
            </Button>
          </div>

          {/* Feedback inbox */}
          <div className="mystical-card p-6" data-testid="admin-feedback">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2">
                <MessageSquare size={16} className="text-gold" />
                <span className="text-gold font-sans text-xs tracking-[0.25em] uppercase">Beta Feedback</span>
              </div>
              <span className="font-sans text-xs text-text-muted">
                {stats.feedback.new_7d} new this week
              </span>
            </div>
            <div className="space-y-3 max-h-[480px] overflow-y-auto pr-2">
              {feedback.length === 0 && (
                <p className="font-sans text-sm text-text-muted text-center py-8">No feedback yet</p>
              )}
              {feedback.map((f, i) => {
                const Icon = FEEDBACK_ICONS[f.feedback_type] || MessageSquare;
                return (
                  <div
                    key={f.feedback_id || i}
                    className="p-4 rounded-xl bg-bone/40 border border-black/8"
                    data-testid={`admin-feedback-${i}`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Icon size={14} className="text-dark" />
                      <Badge className={`${FEEDBACK_COLORS[f.feedback_type] || FEEDBACK_COLORS.other} border text-[10px] font-sans capitalize`}>
                        {f.feedback_type}
                      </Badge>
                      <span className="font-sans text-[10px] text-text-muted ml-auto">
                        {f.created_at ? new Date(f.created_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                      </span>
                    </div>
                    <p className="font-sans text-sm text-dark leading-relaxed whitespace-pre-wrap">{f.message}</p>
                    {f.page && (
                      <p className="font-sans text-[10px] text-text-muted mt-2 truncate">
                        <span className="uppercase tracking-wider mr-2">Page</span>{f.page}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </TabsContent>

        {/* USERS TAB */}
        <TabsContent value="users" className="mt-8 focus-visible:outline-none">
          <UserManagementTable
            onChange={async () => {
              try {
                const s = await api.get('/admin/stats');
                setStats(s.data);
                refreshOpenFlagsCount();
              } catch { /* noop */ }
            }}
          />
        </TabsContent>

        {/* FLAGS TAB */}
        <TabsContent value="flags" className="mt-8 focus-visible:outline-none">
          <FlagsPanel
            onChange={async () => {
              refreshOpenFlagsCount();
              try {
                const s = await api.get('/admin/stats');
                setStats(s.data);
              } catch { /* noop */ }
            }}
          />
        </TabsContent>

        {/* BETA TESTERS TAB */}
        <TabsContent value="beta" className="mt-8 focus-visible:outline-none">
          <BetaTesterPanel />
        </TabsContent>
      </Tabs>

      {/* Reset confirmation modal */}
      {resetState.open && resetState.preview && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-6"
          data-testid="reset-confirm-modal"
          style={{ background: 'rgba(15,16,16,0.55)', backdropFilter: 'blur(8px)' }}
          onClick={() => setResetState({ open: false, preview: null, loading: false, confirmText: '' })}
        >
          <div
            className="bg-bone rounded-t-3xl sm:rounded-3xl p-7 sm:p-8 w-full sm:max-w-xl relative animate-fade-up"
            onClick={(e) => e.stopPropagation()}
            style={{ boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}
          >
            <p className="text-red-700 font-sans text-[10px] tracking-[0.35em] uppercase mb-2">Destructive Action</p>
            <h3 className="font-serif text-2xl sm:text-3xl font-light text-dark leading-tight mb-3 mystical-heading">
              Wipe {resetState.preview.would_delete} test account{resetState.preview.would_delete === 1 ? '' : 's'}?
            </h3>
            <p className="font-sans text-sm text-text-muted leading-relaxed mb-4">
              This deletes their conversations, journal entries, signs, feedback, sessions, and tokens.
              Cannot be undone.
            </p>

            <div className="mb-5 p-3 rounded-xl bg-moss/8 border border-moss/25" data-testid="reset-protected-list">
              <p className="font-sans text-[10px] text-moss tracking-[0.2em] uppercase mb-2">
                Will be KEPT (protected + admins)
              </p>
              <ul className="space-y-1 font-sans text-xs text-dark">
                {resetState.preview.protected.map(e => (
                  <li key={e}>• {e}</li>
                ))}
              </ul>
            </div>

            {resetState.preview.would_delete > 0 && (
              <details className="mb-5 text-xs">
                <summary className="cursor-pointer font-sans text-text-muted">
                  Show all {resetState.preview.would_delete} accounts to be deleted
                </summary>
                <ul className="mt-3 max-h-40 overflow-y-auto space-y-1 font-mono text-[11px] text-text-muted/80 pl-3">
                  {resetState.preview.accounts.map(a => (
                    <li key={a.user_id}>{a.email} <span className="text-text-muted/50">({a.name || '?'})</span></li>
                  ))}
                </ul>
              </details>
            )}

            <label className="block mb-5">
              <span className="font-sans text-xs text-text-muted tracking-wider uppercase">
                Type <code className="px-1 py-0.5 rounded bg-bone border border-black/10 font-mono">DELETE TEST DATA</code> to confirm
              </span>
              <input
                type="text"
                value={resetState.confirmText}
                onChange={(e) => setResetState(s => ({ ...s, confirmText: e.target.value }))}
                data-testid="reset-confirm-input"
                autoFocus
                placeholder="DELETE TEST DATA"
                className="mt-2 w-full h-12 px-3 bg-sand/50 border border-black/8 focus:border-red-400 focus:ring-1 focus:ring-red-300 rounded-xl font-mono text-sm outline-none"
              />
            </label>

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
              <Button
                variant="ghost"
                onClick={() => setResetState({ open: false, preview: null, loading: false, confirmText: '' })}
                data-testid="reset-cancel-button"
                className="font-sans text-xs text-text-muted h-11"
              >
                Cancel
              </Button>
              <Button
                onClick={executeReset}
                disabled={resetState.loading || resetState.confirmText !== 'DELETE TEST DATA' || resetState.preview.would_delete === 0}
                data-testid="reset-execute-button"
                className="bg-red-600 hover:bg-red-700 text-white rounded-xl font-sans text-xs tracking-[0.25em] uppercase h-11 px-6 disabled:opacity-40"
              >
                <Trash2 size={14} className="mr-2" />
                {resetState.loading ? 'Wiping...' : `Wipe ${resetState.preview.would_delete}`}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatTile({ icon: Icon, label, value, hint, accent = 'gold' }) {
  const color = accent === 'moss' ? 'text-moss' : 'text-gold';
  return (
    <div className="glass-card p-5" data-testid={`stat-tile-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon size={14} className={color} />
        <span className="text-text-muted font-sans text-[10px] tracking-[0.2em] uppercase">{label}</span>
      </div>
      <p className="font-serif text-3xl text-dark">{value}</p>
      {hint && <p className="font-sans text-xs text-text-muted mt-1">{hint}</p>}
    </div>
  );
}
